/**
 * Resaltados sobre el documento abierto (CodeMirror).
 *
 * Antes se marcaba sobre el HTML renderizado de la vista previa; desde que la
 * hoja es una sola —se lee y se escribe en el mismo lugar— las marcas viven
 * acá, como decoraciones del editor.
 *
 * Las posiciones se mapean con el changeset de cada transacción, que es lo que
 * `model/highlights.ts` decía desde el principio: editando DENTRO de la app una
 * marca se arrastra con su texto, exacta, sin tener que reencontrarla. La cita
 * (el ancla) sigue siendo la verdad al ABRIR el documento, porque entre sesión
 * y sesión el `.md` pudo cambiar desde afuera.
 *
 * El `.md` no se toca: un resaltado sigue siendo un archivo lateral del vault.
 */

import { Decoration, type DecorationSet, EditorView } from "@codemirror/view";
import { type EditorState, type Range, StateEffect, StateField } from "@codemirror/state";
import {
  type Highlight,
  type HighlightColor,
  highlightClass,
  makeAnchor,
  resolveAnchor,
} from "../model/highlights.ts";

/** Un resaltado ya ubicado en el documento: el modelo más su rango vivo. */
export interface PlacedHighlight {
  id: string;
  color: HighlightColor;
  createdAt: number;
  from: number;
  to: number;
}

/** Reemplaza el juego completo de resaltados del documento. */
export const setHighlights = StateEffect.define<readonly PlacedHighlight[]>();

/** Prende/apaga el modo "hay un fibrón en la mano" (lo lee el Live Preview). */
export const setMarking = StateEffect.define<boolean>();

function decoOf(h: PlacedHighlight): Range<Decoration> {
  return Decoration.mark({
    class: highlightClass(h.color),
    // El modelo viaja en el spec: así el rango y lo que se guarda en disco son
    // la misma cosa y no hay dos listas que mantener en sincronía.
    hl: { id: h.id, color: h.color, createdAt: h.createdAt },
  }).range(h.from, h.to);
}

function setOf(hs: readonly PlacedHighlight[]): DecorationSet {
  return Decoration.set(
    hs.filter((h) => h.to > h.from).map(decoOf),
    true,
  );
}

/**
 * Los rangos resaltados del documento.
 *
 * Al mapear, una marca cuyo texto se borró entero queda vacía y CodeMirror la
 * descarta sola: borrar el texto borra la marca, que es lo que uno espera.
 */
export const highlightsField = StateField.define<DecorationSet>({
  create: () => Decoration.none,
  update(set, tr) {
    let next = set.map(tr.changes);
    for (const e of tr.effects) if (e.is(setHighlights)) next = setOf(e.value);
    return next;
  },
});

/** Si hay marcador en la mano. Vive en el estado para que lo vea el decorador. */
export const markingField = StateField.define<boolean>({
  create: () => false,
  update(value, tr) {
    for (const e of tr.effects) if (e.is(setMarking)) value = e.value;
    return value;
  },
});

/** Las marcas, dibujadas. Va aparte del campo para poder sacarlas en modo crudo. */
export const highlightDecorations = EditorView.decorations.from(highlightsField);

/**
 * El juego inicial de resaltados de un estado nuevo.
 *
 * Se usa como `highlightsField.init(...)` porque al crear el `EditorState` no
 * hay todavía transacción donde despachar un efecto.
 */
export function highlightsOf(hs: readonly PlacedHighlight[]): DecorationSet {
  return setOf(hs);
}

/** Los resaltados ubicados hoy en el documento, en orden. */
export function placedHighlights(state: EditorState): PlacedHighlight[] {
  const out: PlacedHighlight[] = [];
  for (let it = state.field(highlightsField).iter(); it.value; it.next()) {
    const hl = it.value.spec.hl as Omit<PlacedHighlight, "from" | "to"> | undefined;
    if (hl) out.push({ ...hl, from: it.from, to: it.to });
  }
  return out;
}

/**
 * Ubica los resaltados guardados contra el texto del documento.
 *
 * Los que no se pueden reencontrar quedan HUÉRFANOS: no se pintan, pero se
 * devuelven aparte para volver a guardarlos tal cual. Un documento editado
 * afuera no debería costarle a nadie sus marcas de forma silenciosa.
 */
export function placeHighlights(
  doc: string,
  saved: readonly Highlight[],
): { placed: PlacedHighlight[]; orphans: Highlight[] } {
  const placed: PlacedHighlight[] = [];
  const orphans: Highlight[] = [];
  for (const h of saved) {
    const at = h.anchor.kind === "text" ? resolveAnchor(doc, h.anchor) : null;
    if (at) placed.push({ id: h.id, color: h.color, createdAt: h.createdAt, ...at });
    else orphans.push(h);
  }
  return { placed, orphans };
}

/** Los resaltados listos para escribir en el vault (los vivos + los huérfanos). */
export function toHighlights(
  state: EditorState,
  orphans: readonly Highlight[],
): Highlight[] {
  const doc = state.doc.toString();
  const live = placedHighlights(state).map((h) => ({
    id: h.id,
    color: h.color,
    createdAt: h.createdAt,
    anchor: makeAnchor(doc, h.from, h.to),
  }));
  return [...live, ...orphans];
}
